/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

// Insert any custom theme js here...


class HeaderPredictiveSearch extends HTMLElement {
 constructor() {
   super();

   this.form = this.querySelector('form');
   this.input = this.querySelector('input[type="search"]');
   this.predictiveSearchResults = this.querySelector('.header-predictive-search__results');
   this.boundOnDocumentClick = this.onDocumentClick.bind(this);
   this.boundOnKeydown = this.onKeydown.bind(this);
   this.boundOnResultsClick = this.onResultsClick.bind(this);

   this.input.addEventListener('input', this.debounce((event) => {
     this.onChange(event);
   }, 300).bind(this));
 }

 connectedCallback() {
   document.addEventListener('click', this.boundOnDocumentClick);
   document.addEventListener('keydown', this.boundOnKeydown);
   this.predictiveSearchResults.addEventListener('click', this.boundOnResultsClick);
 }

 disconnectedCallback() {
   document.removeEventListener('click', this.boundOnDocumentClick);
   document.removeEventListener('keydown', this.boundOnKeydown);
   this.predictiveSearchResults.removeEventListener('click', this.boundOnResultsClick);
 }

 onChange() {
   const searchTerm = this.input.value.trim();

   if (!searchTerm.length) {
     this.close();
     return;
   }

   this.getSearchResults(searchTerm);
 }

 getSearchResults(searchTerm) {
   fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)
     .then((response) => {
       if (!response.ok) {
         var error = new Error(response.status);
         this.close();
         throw error;
       }

       return response.text();
     })
     .then((text) => {
       const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
       this.predictiveSearchResults.innerHTML = resultsMarkup;
       if (this.predictiveSearchResults.textContent.trim().length) {
         this.open();
       } else {
         this.close();
       }
     })
     .catch((error) => {
       this.close();
       throw error;
     });
 }

 open() {
   this.predictiveSearchResults.style.display = 'block';
   this.input.setAttribute('aria-expanded', 'true');
 }

 close() {
   this.predictiveSearchResults.style.display = 'none';
   this.input.setAttribute('aria-expanded', 'false');
 }

 onDocumentClick(event) {
   if (!this.contains(event.target)) {
     this.close();
   }
 }

 onKeydown(event) {
   if (event.key === 'Escape') {
     this.close();
   }
 }

 onResultsClick(event) {
   const triggerButton = event.target.closest('#predictive-search-trigger');
   if (!triggerButton) return;

   event.preventDefault();

   if (this.form) {
     this.form.requestSubmit();
   }
 }

 debounce(fn, wait) {
   let t;
   return (...args) => {
     clearTimeout(t);
     t = setTimeout(() => fn.apply(this, args), wait);
   };
 }
}

customElements.define('header-predictive-search', HeaderPredictiveSearch);

// Mugs: no colour counts as chosen until the shopper taps one, so the pre-selected
// default can't be bought by accident (it caused wrong-colour returns).
const initChooseColour = (productForm) => {
  const form = productForm.querySelector("form");
  const addButton = productForm.querySelector(".js-product-add");
  const addText = productForm.querySelector(".js-product-add staged-action-text");
  const addWrapper = productForm.querySelector(".js-product-buttons");
  const dynamicButton = productForm.querySelector("dynamic-button");
  const optionName = (productForm.dataset.confirmationOptionName || "").toLowerCase();

  if (!form || !addButton || !optionName) return;

  // form.id is shadowed by the form's hidden <input name="id">, so read the attribute.
  const formId = form.getAttribute("id");
  const inputs = Array.from(document.querySelectorAll(`input[type="radio"][form="${formId}"]`))
    .filter((input) => input.name.toLowerCase() === optionName);

  if (!inputs.length) return;

  const fieldset = inputs[0].closest("fieldset");
  const optionTitle = fieldset?.querySelector(".js-option-title");
  const selectionKey = `${window.location.pathname}::${formId}::${optionName}::chosen`;
  const chooseText = "Choose your inside colour";

  window.productConfirmationSelections = window.productConfirmationSelections || {};

  // Hide the theme's "selected" outline while no colour has been chosen.
  if (!document.getElementById("choose-colour-style")) {
    const style = document.createElement("style");
    style.id = "choose-colour-style";
    style.textContent =
      ".product-form__swatch.is-unchosen input[type=radio]:checked + label::after{content:none}" +
      ".product-form__swatch.is-unchosen .product-form__swatch__item--color input[type=radio]:checked + label{box-shadow:none}";
    document.head.appendChild(style);
  }

  const lock = () => {
    fieldset?.classList.add("is-unchosen");
    if (optionTitle) optionTitle.textContent = "Please choose";
    addButton.disabled = true;
    if (addText) addText.textContent = chooseText;
    if (dynamicButton) {
      dynamicButton.style.pointerEvents = "none";
      dynamicButton.style.opacity = "0.45";
    }
  };

  const unlock = (input) => {
    fieldset?.classList.remove("is-unchosen");
    if (optionTitle) optionTitle.textContent = input.value;
    if (dynamicButton) {
      dynamicButton.style.pointerEvents = "";
      dynamicButton.style.opacity = "";
    }
    // Leave sold-out variants to the theme's own "Sold out" state.
    if (addWrapper && addWrapper.classList.contains("is-disabled")) return;
    addButton.disabled = false;
    if (addText && addText.textContent.trim() === chooseText) {
      addText.textContent = document.querySelector(`[data-add-to-cart-text]`)?.dataset.addToCartText || "Add to cart";
    }
  };

  // A click fires even on the already-selected swatch, so picking the default colour counts.
  inputs.forEach((input) => {
    input.addEventListener("click", () => {
      window.productConfirmationSelections[selectionKey] = true;
      unlock(input);
    });
  });

  if (window.productConfirmationSelections[selectionKey]) {
    const checked = inputs.find((input) => input.checked);
    if (checked) unlock(checked);
  } else {
    lock();
  }

  // Changing another option (e.g. size) makes the theme re-enable Add to cart; re-lock until a colour is chosen.
  document.addEventListener("variant:changed", () => {
    if (!window.productConfirmationSelections[selectionKey]) setTimeout(lock, 0);
  });

  productForm.confirmationReady = true;
};

const initProductConfirmations = () => {
  window.productConfirmationSelections = window.productConfirmationSelections || {};

  document.querySelectorAll("product-form[data-needs-confirmation]").forEach((productForm) => {
    if (productForm.confirmationReady) return;

    // Mug forms confirm a colour option; phone cases confirm Size and keep the tick-box.
    const confirmsColour = /colou?r/.test((productForm.dataset.confirmationOptionName || "").toLowerCase());

    if (confirmsColour) {
      initChooseColour(productForm);
      return;
    }

    const form = productForm.querySelector("form");
    const checkbox = productForm.querySelector("[data-confirmation-checkbox]");
    const addButton = productForm.querySelector(".js-product-add");
    const addWrapper = productForm.querySelector(".js-product-buttons");
    const confirmationBlock = productForm.querySelector("[data-product-confirmation]");
    const selectedText = productForm.querySelector("[data-confirmation-selected]");
    const dynamicButton = productForm.querySelector("dynamic-button");
    const confirmationOptionName = (productForm.dataset.confirmationOptionName || "").toLowerCase();
    const fallbackLabel = productForm.dataset.confirmationFallbackLabel || "Selected Option";
    const selectionKey = `${window.location.pathname}::${form?.id || ""}::${confirmationOptionName}`;

    if (!form || !checkbox || !addButton) return;

    const getConfirmationSelect = () => {
      const directSelect = form.id
        ? document.querySelector(`[data-confirmation-option-select][form="${form.id}"]`)
        : document.querySelector("[data-confirmation-option-select]");

      if (directSelect) return directSelect;

      const formSelects = Array.from(form.querySelectorAll("select"));
      const associatedSelects = form.id ? Array.from(document.querySelectorAll(`select[form="${form.id}"]`)) : [];
      const selects = [ ...formSelects, ...associatedSelects ];

      return selects.find((select) => {
        const normalizedName = select.name.toLowerCase().replace(/^options\[/, "").replace(/\]$/, "");

        return confirmationOptionName && normalizedName === confirmationOptionName;
      });
    };

    const confirmationSelect = getConfirmationSelect();

    if (confirmationSelect) {
      if (!confirmationSelect.querySelector('option[value=""]')) {
        const placeholder = document.createElement("option");
        const selectedOption = confirmationSelect.options[confirmationSelect.selectedIndex];

        placeholder.value = "";
        placeholder.textContent = "Please select";
        placeholder.disabled = true;
        placeholder.dataset.optionValueId = selectedOption?.dataset.optionValueId || "";
        confirmationSelect.insertBefore(placeholder, confirmationSelect.firstChild);
      }

      const placeholderSelected = confirmationSelect.options[confirmationSelect.selectedIndex]?.value === "";

      if (!placeholderSelected && window.productConfirmationSelections[selectionKey]) {
        confirmationSelect.value = window.productConfirmationSelections[selectionKey];
      } else {
        confirmationSelect.value = "";
      }
    }

    const getSelectedOption = () => {
      if (confirmationSelect && confirmationSelect.value === "") {
        return "";
      }

      const data = new FormData(form);

      for (const [key, value] of data.entries()) {
        const normalizedKey = key.toLowerCase().replace(/^options\[/, "").replace(/\]$/, "");

        if (confirmationOptionName && normalizedKey === confirmationOptionName) {
          return value;
        }
      }

      return fallbackLabel;
    };


    const updateState = () => {
  const selectedOption = getSelectedOption();
  const hasSelectedOption = selectedOption !== "" && selectedOption !== fallbackLabel;

  const variantStyle =
  productForm.dataset.confirmationVariantStyle || "";

const isSwatchProduct =
  variantStyle === "buttons" ||
  variantStyle === "swatches-buttons" ||
  variantStyle === "swatches-dropdowns";
  
  const unavailable = addWrapper && addWrapper.classList.contains("is-disabled");

  if (confirmationBlock) {
    if (isSwatchProduct) {
      confirmationBlock.hidden = false;
    } else {
      confirmationBlock.hidden = !hasSelectedOption;
    }
  }

  if (selectedText) {
    selectedText.textContent = hasSelectedOption ? selectedOption : fallbackLabel;
  }

  let canPurchase = false;

  if (isSwatchProduct) {
    canPurchase = checkbox.checked;
  } else {
    canPurchase = hasSelectedOption && checkbox.checked;
  }

  addButton.disabled = !canPurchase || unavailable;

  if (dynamicButton) {
    dynamicButton.style.pointerEvents = canPurchase && !unavailable ? "" : "none";
    dynamicButton.style.opacity = canPurchase && !unavailable ? "" : "0.45";
  }
};

console.log("confirmationSelect", confirmationSelect);
console.log("confirmationOptionName", confirmationOptionName);
    checkbox.addEventListener("change", updateState);

    if (confirmationSelect) {
      confirmationSelect.addEventListener("change", () => {
        if (confirmationSelect.value !== "") {
          window.productConfirmationSelections[selectionKey] = confirmationSelect.value;
        }

        checkbox.checked = false;
        setTimeout(updateState, 0);
      });
    }

    form.addEventListener("change", (event) => {
      if (event.target === checkbox) return;

      checkbox.checked = false;
      setTimeout(updateState, 0);
    });

    productForm.confirmationReady = true;
    updateState();
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initProductConfirmations);
} else {
  initProductConfirmations();
}

document.addEventListener("on:variant:change", () => {
  setTimeout(initProductConfirmations, 0);
}, true);